/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, FormHelperText, Grid, debounce } from "@mui/material"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FormikHelpers, useFormik } from "formik"
import { useCallback, useMemo, useState } from "react"
import giftApis from "src/api/gifts"
import { GiftSelection, ShopSelection } from "src/components"
import CommonContainer from "src/components/CommonContainer"
import {
  GIFTS_BY_CUSTOMER,
  GIFT_EXCHANGE_HISTORY,
  GIFT_ORDER,
} from "src/constants/queryKeys"
import { useAuthContext, useToastify } from "src/hooks"
import { Button, Text } from "src/shared"
import {
  GiftListByCustomerModel,
  InsertGiftOrderModel,
  ProductInsertModel,
} from "src/types"

const GiftExchange = () => {
  const [totalTemUsed, setTotalTemUsed] = useState(0)
  const { profile, onConfirmPhoneNumber } = useAuthContext()
  const { showError, showSuccess } = useToastify()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (payload: InsertGiftOrderModel) =>
      giftApis.giftExchange(payload),
  })

  const handleInvalidateQuery = useCallback(async () => {
    await onConfirmPhoneNumber(profile?.Mobile || "")
    queryClient.invalidateQueries({
      queryKey: [GIFT_ORDER],
    })
    queryClient.invalidateQueries({
      queryKey: [GIFT_EXCHANGE_HISTORY],
    })

    queryClient.invalidateQueries({
      queryKey: [GIFTS_BY_CUSTOMER],
    })
  }, [onConfirmPhoneNumber, queryClient, profile])

  const debounceInvalidateQuery = debounce(handleInvalidateQuery, 2000)

  const handleSubmit = useCallback(
    async (
      val: InsertGiftOrderModel,
      formilHelper: FormikHelpers<InsertGiftOrderModel>
    ) => {
      if (
        !!val.lstProduct &&
        !!val.lstProduct.length &&
        !!val.customerPhone &&
        !!val.stockCode
      ) {
        await mutateAsync(val, {
          onSuccess: async d => {
            if (d?.ErrorCode === 0) {
              showSuccess("Đổi quà thành công!")
              await debounceInvalidateQuery()
              formilHelper.resetForm(undefined)
              setTotalTemUsed(0)
            } else if (d?.ErrorCode === 1 && !!d?.ErrorMessage) {
              showError(d?.ErrorMessage)
            } else {
              showError("Đã xảy ra lỗi, vui lòng thử lại!")
            }
          },
          onError: () => {
            showError("Đã xảy ra lỗi, vui lòng thử lại!")
          },
        })
      }
    },
    [mutateAsync, showSuccess, debounceInvalidateQuery, showError]
  )

  const { setFieldValue, submitForm, values } = useFormik<InsertGiftOrderModel>(
    {
      initialValues: {
        createUser: "",
        customerPhone: profile?.Mobile || "",
        lstProduct: [],
        stockCode: "",
      },
      onSubmit: handleSubmit,
    }
  )

  const handleGiftChange = useCallback(
    (vals?: GiftListByCustomerModel[]) => {
      if (!vals || !vals.length) {
        setFieldValue("lstProduct", undefined)
        setTotalTemUsed(0)
        return
      }
      const giftList = vals.map(
        v =>
          ({
            productCode: v.ProductCode,
            productName: v.ProductName,
            productImage: v.ProductDefaultImage,
            quantity: 1,
            stockCode: v.StockCode,
          }) as ProductInsertModel
      )
      setFieldValue("lstProduct", giftList || undefined)

      const totalTemUsed = vals.reduce((a, b) => a + (b.TemQuantity || 0), 0)
      setTotalTemUsed(totalTemUsed)
    },
    [setFieldValue]
  )

  const onShopChange = (storeCode?: string) => {
    setFieldValue("stockCode", storeCode)
    setFieldValue("lstProduct", undefined)
  }

  const disableSubmit = useMemo(
    () =>
      !values.lstProduct ||
      !values.lstProduct.length ||
      !values.stockCode ||
      !!(totalTemUsed > (profile?.TemQuantity || 0)),
    [values, profile, totalTemUsed]
  )

  const giftValue = useMemo(
    () => values?.lstProduct?.map(x => x.productCode) || [],
    [values]
  )

  const shopValue = useMemo(() => values?.stockCode || "", [values])

  return (
    <CommonContainer
      bannerProps={{
        src: "/assets/images/user_info_banner.png",
        alt: "doi_qua_hap_dan",
      }}
    >
      <Grid
        sx={theme => ({
          padding: theme.spacing(0, 4),
        })}
        rowGap={4}
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Text
          sx={theme => ({
            "& > span": {
              color: theme.palette.info.main,
            },
          })}
          fontWeight={700}
          textAlign="center"
        >
          Chúc mừng khách hàng {profile?.Mobile} đã tích luỹ được{" "}
          <span> {profile?.TemQuantity || 0} tem</span>
        </Text>
        <Grid
          spacing={3}
          container
          flexDirection="column"
          alignItems="center"
          justifyContent="space-between"
          sx={() => ({
            "& > div": {
              width: "100%",
            },
          })}
        >
          <Grid item>
            <ShopSelection value={shopValue} onChange={onShopChange} />
          </Grid>
          <Grid item>
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              justifyContent="center"
            >
              <GiftSelection
                value={giftValue}
                stockCode={values?.stockCode}
                onChange={handleGiftChange}
              />
              {totalTemUsed > (profile?.TemQuantity || 0) && (
                <FormHelperText error>
                  Tổng số tem được sử dụng là {totalTemUsed} vượt quá số lượng
                  tem bạn đang có là {profile?.TemQuantity}.
                </FormHelperText>
              )}
            </Box>
          </Grid>
        </Grid>
        <Button
          onClick={submitForm}
          disabled={isPending || disableSubmit}
          loading={isPending}
        >
          Đổi quà
        </Button>
      </Grid>
    </CommonContainer>
  )
}

export default GiftExchange
